import React, { useState, useEffect } from 'react';
import { getOwnerPaymentLogs } from '../../Services/Operations/ProductAPI';
import { toast } from 'react-toastify';
import '../../Styles/OwnerPaymentLog.css';

const OwnerPaymentLogModal = ({ ownerId, ownerName, onClose, token }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalLogs, setTotalLogs] = useState(0);
  const [expandedLog, setExpandedLog] = useState(null);
  const [ownerDetails, setOwnerDetails] = useState(null);
  const [summary, setSummary] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');

  const fetchLogs = async (page = 1, status = '') => {
    setLoading(true);
    try {
      const response = await getOwnerPaymentLogs(ownerId, token, page, 20, status);
      if (response.success) {
        setLogs(response.data || []);
        setTotalPages(response.pagination?.pages || 1);
        setTotalLogs(response.pagination?.total || 0);
        setCurrentPage(page);
        setOwnerDetails(response.ownerDetails || null);
        setSummary(response.summary || null);
      }
    } catch (error) {
      toast.error('Failed to fetch payment logs');
      console.error('Error fetching logs:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs(1);
  }, [ownerId]);

  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    fetchLogs(1, status);
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  };

  const getActionBadge = (action) => {
    const styles = {
      PAYMENT: { color: '#28a745', bg: '#d4edda', icon: '💰' },
      PAYOUT: { color: '#17a2b8', bg: '#d1ecf1', icon: '💳' },
      UPDATE: { color: '#17a2b8', bg: '#d1ecf1', icon: '📝' },
      CREATE: { color: '#28a745', bg: '#d4edda', icon: '✅' },
      DELETE: { color: '#dc3545', bg: '#f8d7da', icon: '🗑️' },
    };
    const style = styles[action] || { color: '#6c757d', bg: '#e9ecef', icon: '📋' };
    return (
      <span className="log-action-badge" style={{ 
        backgroundColor: style.bg, 
        color: style.color,
        padding: '4px 12px',
        borderRadius: '20px',
        fontSize: '12px',
        fontWeight: '600',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px'
      }}>
        {style.icon} {action}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    const styles = {
      completed: { color: '#28a745', bg: '#d4edda' },
      pending: { color: '#ffc107', bg: '#fff3cd' },
      failed: { color: '#dc3545', bg: '#f8d7da' },
      cancelled: { color: '#6c757d', bg: '#e9ecef' },
    };
    const style = styles[status] || { color: '#6c757d', bg: '#e9ecef' };
    return (
      <span style={{
        backgroundColor: style.bg,
        color: style.color,
        padding: '2px 10px',
        borderRadius: '12px',
        fontSize: '11px',
        fontWeight: '500',
        textTransform: 'capitalize'
      }}>
        {status || 'unknown'}
      </span>
    );
  };

  const renderPaymentDetails = (changes) => {
    if (!changes || !changes.after) return null;
    
    const after = changes.after;
    return (
      <div className="payment-details">
        <div className="payment-amount">
          <span className="amount-label">Amount:</span>
          <span className="amount-value">₹{after.amount?.toLocaleString() || 0}</span>
        </div>
        <div className="payment-method">
          <span className="method-label">Method:</span>
          <span className="method-value">{after.paymentMethod || 'N/A'}</span>
        </div>
        {after.transactionId && (
          <div className="payment-transaction">
            <span className="txn-label">Transaction ID:</span>
            <span className="txn-value">{after.transactionId}</span>
          </div>
        )}
        {after.reference && (
          <div className="payment-reference">
            <span className="ref-label">Reference:</span>
            <span className="ref-value">{after.reference}</span>
          </div>
        )}
        {after.paymentDate && (
          <div className="payment-date">
            <span className="date-label">Date:</span>
            <span className="date-value">{formatDate(after.paymentDate)}</span>
          </div>
        )}
        {after.notes && (
          <div className="payment-notes">
            <span className="notes-label">Notes:</span>
            <span className="notes-value">{after.notes}</span>
          </div>
        )}
        <div className="payment-status">
          <span className="status-label">Status:</span>
          {getStatusBadge(after.status)}
        </div>
      </div>
    );
  };

  return (
    <div className="modal-overlay log-modal-overlay" onClick={onClose}>
      <div className="modal-content log-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header log-modal-header">
          <div className="header-content">
            <h3>Commission Payment Logs</h3>
            <span className="route-name-badge">{ownerName}</span>
          </div>
          <button className="modal-close-btn" onClick={onClose}>×</button>
        </div>

        <div className="modal-body log-modal-body">

          {ownerDetails && (
            <div className="owner-summary">
              <div className="summary-grid">
                <div className="summary-item">
                  <span className="summary-label">Total Commission</span>
                  <span className="summary-value">₹{ownerDetails.totalCommissionEarned?.toLocaleString() || 0}</span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Paid</span>
                  <span className="summary-value paid">₹{ownerDetails.paidCommission?.toLocaleString() || 0}</span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Pending</span>
                  <span className="summary-value pending">₹{ownerDetails.pendingCommission?.toLocaleString() || 0}</span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Commission Rate</span>
                  <span className="summary-value">{ownerDetails.commissionRate || 0}%</span>
                </div>
              </div>
            </div>
          )}


          {summary && (
            <div className="log-stats">
              <div className="stat-item">
                <span className="stat-label">Total Payments</span>
                <span className="stat-value">{summary.totalPayments || 0}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Total Amount</span>
                <span className="stat-value">₹{summary.totalAmount?.toLocaleString() || 0}</span>
              </div>
              {summary.byStatus && Object.keys(summary.byStatus).length > 0 && (
                <div className="stat-item">
                  <span className="stat-label">Status</span>
                  <span className="stat-value">
                    {Object.entries(summary.byStatus).map(([status, data]) => (
                      <span key={status} style={{ marginRight: '8px' }}>
                        {status}: {data.count}
                      </span>
                    ))}
                  </span>
                </div>
              )}
            </div>
          )}


          <div className="filter-section">
            <span className="filter-label">Filter by Status:</span>
            <button 
              className={`filter-btn ${statusFilter === '' ? 'active' : ''}`}
              onClick={() => handleStatusFilter('')}
            >
              All
            </button>
            <button 
              className={`filter-btn ${statusFilter === 'completed' ? 'active' : ''}`}
              onClick={() => handleStatusFilter('completed')}
            >
              Completed
            </button>
            <button 
              className={`filter-btn ${statusFilter === 'pending' ? 'active' : ''}`}
              onClick={() => handleStatusFilter('pending')}
            >
              Pending
            </button>
            <button 
              className={`filter-btn ${statusFilter === 'failed' ? 'active' : ''}`}
              onClick={() => handleStatusFilter('failed')}
            >
              Failed
            </button>
            <button 
              className={`filter-btn ${statusFilter === 'cancelled' ? 'active' : ''}`}
              onClick={() => handleStatusFilter('cancelled')}
            >
              Cancelled
            </button>
          </div>

          {loading ? (
            <div className="log-loading">
              <div className="spinner"></div>
              <p>Loading payment logs...</p>
            </div>
          ) : logs.length === 0 ? (
            <div className="no-logs">
              <div className="no-logs-icon">📭</div>
              <p>No payment logs found for this owner</p>
            </div>
          ) : (
            <div className="logs-list">
              {logs.map((log) => (
                <div key={log._id} className="log-item">
                  <div className="log-header-row">
                    <div className="log-left">
                      {getActionBadge(log.action)}
                      <span className="log-timestamp">{formatDate(log.createdAt)}</span>
                    </div>
                    <div className="log-right">
                      <button 
                        className="expand-btn"
                        onClick={() => setExpandedLog(expandedLog === log._id ? null : log._id)}
                      >
                        {expandedLog === log._id ? '▲' : '▼'}
                      </button>
                    </div>
                  </div>
                  
                  <div className="log-meta">
                    <div className="user-info">
                      <span className="user-name">{log.performedBy?.userName || 'Unknown'}</span>
                      <span className="user-type">{log.performedBy?.userType || 'N/A'}</span>
                    </div>
                    <span className="log-version">Version {log.version}</span>
                    {log.ipAddress && (
                      <span className="log-ip">IP: {log.ipAddress}</span>
                    )}
                  </div>

                  {renderPaymentDetails(log.changes)}

                  {log.changes?.summary && (
                    <div className="change-summary">
                      <strong>Summary:</strong> {log.changes.summary}
                    </div>
                  )}

                  {expandedLog === log._id && (
                    <div className="log-expanded-details">
                      <div className="detail-section">
                        <h5>Full Payment Data</h5>
                        <pre className="log-json">
                          {JSON.stringify(log, null, 2)}
                        </pre>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {!loading && logs.length > 0 && (
            <div className="log-pagination">
              <button 
                className="pagination-btn"
                onClick={() => fetchLogs(currentPage - 1, statusFilter)}
                disabled={currentPage <= 1}
              >
                Previous
              </button>
              <span className="page-info">
                Page {currentPage} of {totalPages}
              </span>
              <button 
                className="pagination-btn"
                onClick={() => fetchLogs(currentPage + 1, statusFilter)}
                disabled={currentPage >= totalPages}
              >
                Next
              </button>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="modal-close-btn secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default OwnerPaymentLogModal;